import { Box, Typography } from "@mui/material";
import {
  ContactSupportButton,
  UpgradeSubscriptionButton,
} from "components/action-buttons/action-buttons";
import { Reason } from "data/models";
import React from "react";
import { AnyPermissionsKey } from "./permission-keys";

// These permission reasons are in priority order. The first triggered reason will be shown to the user first.
export const PermissionsReasons = {
  FEATURE_DISABLED: "feature_disabled",
  EXTERNALLY_MANAGED: "externally_managed",
  UPGRADE_REQUIRED: "upgrade_required",
  FREE_TRIAL: "free_trial",
  ROLE_DENY: "role_deny",
} as const satisfies Record<string, Reason>;

export type PermissionMessageTemplateFn = (
  content: string,
  action: React.ReactNode
) => React.ReactNode;

const defaultMessageTemplate = (content: String, action: React.ReactNode) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography>{content}</Typography>
      </Box>
      {action}
    </Box>
  );
};

const externallyManagedMessage = (
  permissionKey: AnyPermissionsKey,
  templateFn: PermissionMessageTemplateFn = defaultMessageTemplate
): React.ReactNode => {
  return templateFn(
    `Your subscription is managed as part of your contract. 
    For more details, please refer to your contract or contact support regarding ${permissionKey.resourceDisplay}.`,
    <ContactSupportButton />
  );
};

const upgradeRequiredMessage = (
  permissionKey: AnyPermissionsKey,
  templateFn: PermissionMessageTemplateFn = defaultMessageTemplate
): React.ReactNode => {
  return templateFn(
    `You do not have access to ${permissionKey.resourceDisplay} as part of your subscription. 
    Please upgrade your subscription to gain access.`,
    <UpgradeSubscriptionButton />
  );
};

const featureDisabledMessage = (
  permissionKey: AnyPermissionsKey,
  templateFn: PermissionMessageTemplateFn = defaultMessageTemplate
): React.ReactNode => {
  return templateFn(
    `The ability to ${permissionKey.actionDisplay} ${permissionKey.resourceDisplay} is currently disabled. 
    Please contact support for more details.`,
    <ContactSupportButton />
  );
};

const freeTrialDisabledMessage = (
  permissionKey: AnyPermissionsKey,
  templateFn: PermissionMessageTemplateFn = defaultMessageTemplate
): React.ReactNode => {
  return templateFn(
    `The ability to ${permissionKey.actionDisplay} ${permissionKey.resourceDisplay} is disabled for trial accounts. 
    Please contact support for more details.`,
    <ContactSupportButton />
  );
};

const roleDenyMessage = (
  permissionKey: AnyPermissionsKey,
  templateFn: PermissionMessageTemplateFn = defaultMessageTemplate
): React.ReactNode => {
  return templateFn(
    `Your organisation role does not grant access to ${permissionKey.actionDisplay} ${permissionKey.resourceDisplay}.`,
    <ContactSupportButton />
  );
};

// The default permission reason messages shown to the user
export const PermissionReasonMessages: Record<
  Reason,
  (
    permissionKey: AnyPermissionsKey,
    templateFn?: PermissionMessageTemplateFn
  ) => React.ReactNode
> = {
  [PermissionsReasons.FEATURE_DISABLED]: featureDisabledMessage,
  [PermissionsReasons.EXTERNALLY_MANAGED]: externallyManagedMessage,
  [PermissionsReasons.UPGRADE_REQUIRED]: upgradeRequiredMessage,
  [PermissionsReasons.FREE_TRIAL]: freeTrialDisabledMessage,
  [PermissionsReasons.ROLE_DENY]: roleDenyMessage,
};

// The default permission reason message strings shown to the user
export const PermissionReasonMessagesString: Record<Reason, String> = {
  [PermissionsReasons.FEATURE_DISABLED]: "This feature has been disabled.",
  [PermissionsReasons.EXTERNALLY_MANAGED]:
    "This feature is managed by support as part of your subscription.",
  [PermissionsReasons.UPGRADE_REQUIRED]:
    "This feature requires a subscription upgrade.",
  [PermissionsReasons.FREE_TRIAL]:
    "This feature is not available on a trial subscription",
  [PermissionsReasons.ROLE_DENY]:
    "Your organisation role does not grant access to this feature.",
};
